"use client";

import { useState, useEffect } from "react";
import { ArrowRight, Loader, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
function AscentForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-primary/40 bg-background/60 backdrop-blur-md p-8 shadow-2xl">
        <p className="font-serif text-xl text-foreground mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem className="scroll-m-4">
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary block">
                Full Name *
              </FormLabel>
              <FormControl>
                <Input placeholder="John Doe" onFocus={e => e.target.scrollIntoView({
            behavior: "smooth",
            block: "center"
          })} className="h-12 md:h-14 bg-transparent border-0 px-0 font-mono text-sm text-primary placeholder:text-foreground/40 focus-visible:ring-0 focus-visible:border-primary block" {...field} />
              </FormControl>
              <FormDescription className="font-mono text-[10px] uppercase tracking-wider text-primary">
                Your legal name for our records.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem className="scroll-m-4">
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary block">
                Email Address *
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="Enter your email address" onFocus={e => e.target.scrollIntoView({
            behavior: "smooth",
            block: "center"
          })} className="h-12 md:h-14 bg-transparent border-0 px-0 font-mono text-sm text-primary placeholder:text-foreground/40 focus-visible:ring-0 focus-visible:border-primary block" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="scroll-m-4">
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary block">
                Phone Number *
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 555-000-0000" onFocus={e => e.target.scrollIntoView({
            behavior: "smooth",
            block: "center"
          })} className="h-12 md:h-14 bg-transparent border-0 px-0 font-mono text-sm text-primary placeholder:text-foreground/40 focus-visible:ring-0 focus-visible:border-primary block" {...field} />
              </FormControl>
              <FormDescription className="font-mono text-[10px] uppercase tracking-wider text-primary">
                We will reach out for beta access.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="experience_level" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary block">
                Creative Experience *
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger onFocus={e => e.target.scrollIntoView({
              behavior: "smooth",
              block: "center"
            })} className="h-12 md:h-14 bg-transparent border-0 px-0 font-mono text-sm text-primary focus:ring-0 focus-visible:ring-0 focus-visible:border-primary block">
                    <SelectValue className="text-primary" placeholder="Select your background" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="Novice – Just starting out" className="font-mono text-xs">Novice – Just starting out</SelectItem>
                  <SelectItem value="Intermediate – Some film experience" className="font-mono text-xs">Intermediate – Some film experience</SelectItem>
                  <SelectItem value="Professional – Industry veteran" className="font-mono text-xs">Professional – Industry veteran</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem className="md:col-span-2 scroll-m-4">
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary block">
                Beta Launch Interest Date
              </FormLabel>
              <FormControl>
                <Input type="date" onFocus={e => e.target.scrollIntoView({
            behavior: "smooth",
            block: "center"
          })} className="h-12 md:h-14 bg-transparent border-0 px-0 font-mono text-sm text-primary placeholder:text-foreground/40 focus-visible:ring-0 focus-visible:border-primary block" {...field} />
              </FormControl>
              <FormDescription className="font-mono text-[10px] uppercase tracking-wider text-primary">
                When are you ready to start creating?
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="gear_rental" render={({
        field
      }) => <FormItem className="md:col-span-2 flex flex-row items-start gap-3 pt-2">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-1 border-foreground/60 data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground" />
              </FormControl>
              <div className="space-y-1">
                <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary cursor-pointer block">
                  Interested in early access tools?
                </FormLabel>
                <FormDescription className="font-mono text-[10px] uppercase tracking-wider text-primary">
                  Check to receive exclusive beta software access.
                </FormDescription>
              </div>
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 border border-primary/40 bg-primary/10">
          <p className="font-mono text-xs uppercase tracking-wider text-primary">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto h-12 md:h-16 px-6 md:px-10 bg-primary text-black md:text-primary-foreground hover:bg-primary/90 font-mono text-sm uppercase tracking-widest shadow-2xl">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing...
          </> : <>
            Join the Beta
            <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}
export default function JoinTheBetaMovement() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  useEffect(() => {
    const handleOpenModal = () => setIsModalOpen(true);
    window.addEventListener("open-beta-modal", handleOpenModal);
    return () => window.removeEventListener("open-beta-modal", handleOpenModal);
  }, []);
  return <>
    <svg className="absolute h-0 w-0 overflow-hidden" aria-hidden="true">
      <defs>
        <filter id="textOutlineFilter">
          <feMorphology operator="dilate" radius="2" in="SourceAlpha" result="thicken" />
          <feFlood floodColor="var(--primary)" result="floodFill" />
          <feComposite in="floodFill" in2="thicken" operator="in" result="outline" />
          <feMerge>
            <feMergeNode in="outline" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
    </svg>
    <section data-section-id="2167" id="join-the-beta-movement" className="relative bg-background text-foreground overflow-hidden min-h-[70vh] flex items-center py-20 md:py-28">
      <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700347/img/studio_workspace_banner.png)]" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-b from-background/85 via-background/75 to-background/95" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/40 to-background/90" aria-hidden="true" />

      <div className="hidden lg:block absolute right-8 bottom-12 z-10" style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)"
      }} aria-hidden="true">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-foreground/50">
          ◆ Ruchnii Studios / Beta Movement / 2024
        </span>
      </div>

      <div className="absolute top-0 left-0 right-0 h-px bg-foreground/10" aria-hidden="true" />

      <div ref={ref} className={`relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="max-w-[820px] mx-auto text-center">
          <div className="inline-flex items-center gap-3 mb-8">
            <span className="size-2 bg-primary" aria-hidden="true" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-foreground/70">
              [ Beta Access / 01 ]
            </span>
            <span className="size-2 bg-primary" aria-hidden="true" />
          </div>

      {}
  <h2 className="font-default font-bold uppercase leading-[0.88] tracking-tight mb-8" style={{
            fontSize: "clamp(3rem, 8vw, 6rem)"
          }}>
  <span className="block text-foreground">Create Your</span>
  <span className="block text-foreground mb-2">Own /</span>

  <svg viewBox="0 0 1200 360" className="block w-full h-auto overflow-visible" role="img" aria-label="Disney and Pixar Styled Films">
    <defs>
      <filter id="blueNeonGlow" x="-40%" y="-40%" width="180%" height="180%">
        <feDropShadow dx="0" dy="0" stdDeviation="1" floodColor="#EFFFFF" floodOpacity="1" />
        <feDropShadow dx="0" dy="0" stdDeviation="2.5" floodColor="#00F6FF" floodOpacity="1" />
        <feDropShadow dx="0" dy="0" stdDeviation="5" floodColor="#009DFF" floodOpacity="0.95" />
        <feDropShadow dx="0" dy="0" stdDeviation="9" floodColor="#005CFF" floodOpacity="0.9" />
        <feDropShadow dx="0" dy="0" stdDeviation="16" floodColor="#001DFF" floodOpacity="0.85" />
      </filter>
    </defs>

    {}
    <text x="600" y="145" textAnchor="middle" fontSize="150" fontFamily="League Spartan, Montserrat, Arial, sans-serif" fontWeight="900" letterSpacing="2" fill="none" stroke="#00F6FF" strokeWidth="5" strokeLinejoin="round" strokeLinecap="round" opacity="1" filter="url(#blueNeonGlow)">
      DISNEY &amp; PIXAR
    </text>

    {}
    <text x="600" y="145" textAnchor="middle" fontSize="150" fontFamily="League Spartan, Montserrat, Arial, sans-serif" fontWeight="900" letterSpacing="2" fill="none" stroke="#EFFFFF" strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round">
      DISNEY &amp; PIXAR
    </text>

    {}
    <text x="600" y="285" textAnchor="middle" fontSize="150" fontFamily="League Spartan, Montserrat, Arial, sans-serif" fontWeight="900" letterSpacing="2" fill="none" stroke="#00F6FF" strokeWidth="5" strokeLinejoin="round" strokeLinecap="round" opacity="1" filter="url(#blueNeonGlow)">
      STYLED FILMS
    </text>

    {}
    <text x="600" y="285" textAnchor="middle" fontSize="150" fontFamily="League Spartan, Montserrat, Arial, sans-serif" fontWeight="900" letterSpacing="2" fill="none" stroke="#EFFFFF" strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round">
      STYLED FILMS
    </text>
  </svg>
</h2>

          <p className="font-serif italic text-xl md:text-2xl lg:text-3xl text-foreground/80 leading-relaxed mb-12 max-w-[640px] mx-auto">
            Unlock the power of professional animation tools. Join the waitlist for our upcoming beta release.
          </p>

          <div className="flex justify-center">
            <Button variant="ghost" onClick={() => setIsModalOpen(true)} className="h-40 md:h-48 px-24 md:px-32 text-black md:text-primary hover:bg-primary/90 font-mono text-3xl md:text-5xl uppercase tracking-widest shadow-2xl block neon-blue-outline-small">
              JOIN THE BETA WAITLIST
              <ArrowRight className="ml-6 size-12 md:size-16" />
            </Button>
          </div>

          <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
            <DialogContent className="max-w-3xl max-h-[100dvh] bg-background border-primary/20 p-0 overflow-y-auto">
              <div className="relative p-4 md:p-10 pb-20 md:pb-12 overflow-y-auto">
                <DialogHeader className="mb-8 text-left">
                  <DialogTitle className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-2">
                    [ Beta Registration ]
                  </DialogTitle>
                  <DialogDescription className="font-serif italic text-xl text-foreground/90">
                    Secure your place in the next generation of cinematic creation.
                  </DialogDescription>
                </DialogHeader>

                <FormProvider formKey="beta_waitlist_form_home" formId="beta_waitlist_form_home" sectionName="join-the-beta-movement" defaultValues={{
                  full_name: "",
                  email: "",
                  phone: "",
                  experience_level: "",
                  preferred_date: "",
                  gear_rental: false
                }} validationRules={{
                  full_name: {
                    required: true,
                    minLength: 2,
                    maxLength: 100
                  },
                  email: {
                    required: true,
                    pattern: "email"
                  },
                  phone: {
                    required: true,
                    pattern: "phone"
                  },
                  experience_level: {
                    required: true
                  }
                }} submitText="Join the Beta" successMessage="Request received. You are now on the list for the Ruchnii Studios beta." errorMessage="Connection failed. Please try again.">
                  <div className="bg-card/50 backdrop-blur-sm">
                    <AscentForm />
                  </div>
                </FormProvider>
              </div>
            </DialogContent>
          </Dialog>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-foreground/60">
              Privacy Guaranteed
            </span>
            <span className="size-1.5 bg-primary" aria-hidden="true" />
            <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-foreground/60">
              Beta Access Soon
            </span>
            <span className="size-1.5 bg-primary" aria-hidden="true" />
            <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-foreground/60">
              10,000+ Creators
            </span>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-primary" aria-hidden="true" />
    </section>
    </>;
}